/**
 * The Players screen's paging rules (the Players screen's Phase 0, `docs/performance/players-screen-phase0.md`).
 * The screen reads one page of the room's players at a time from `POST …/players/records`; what it shows
 * while a page is on its way, which page it asks for, and what an empty table says are decided here.
 */

/** Players to a page. The server's default, and in the query key so a different size is a different page. */
export const PLAYERS_PAGE_SIZE = 50;

/**
 * How long the search box waits for typing to pause before it asks (known gap 64, #153). At 250 ms an ordinary
 * pace — about 300 ms a key — sent `s`, `sm` and `smi` for one word; 400 ms outlasts that pause and still answers
 * well inside half a second of the last key. The results editor's `PlayerPicker` keeps its own 250 ms.
 */
export const PLAYERS_SEARCH_DEBOUNCE_MS = 400;

/** A page the operator chose, and the term it was chosen under. */
export interface PageChoice {
  term: string;
  page: number;
}

/**
 * `loading` before any answer has arrived; `failed` when that first answer failed; `updating` while the
 * previous page stands in for the one asked for (dimmed, with an indicator); `ready` on its own answer.
 */
export type RecordsStatus = 'loading' | 'failed' | 'updating' | 'ready';

/** The page the last player is on; 1 when there are none, since an empty table is still page 1. */
export function lastPage(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * The page to show under the term being searched for: the chosen one while the term is the one it was
 * chosen under, and the first page once the term changes. An edit changes no term, so the screen stays on
 * the page the operator was reading and that page refetches.
 */
export function pageForTerm(choice: PageChoice, settledTerm: string): number {
  return choice.term === settledTerm ? choice.page : 1;
}

/**
 * The page to ask for: the chosen one, unless the last answer's total says there are now fewer pages. A
 * player renamed out of the term being searched, by this operator or another, can leave the operator past
 * the end (no route deletes a player), where the server answers no rows under a total saying there are
 * players. Then the last page, or the first when nothing is left.
 */
export function settledPage(chosen: number, knownTotal: number | null, pageSize: number): number {
  return knownTotal === null ? chosen : Math.min(chosen, lastPage(knownTotal, pageSize));
}

/**
 * What an empty table says, from the total and whether a term was sent. "None registered" sends an operator
 * to Add player, "none match" to the search box, so the two are never one sentence. A term of spaces is no
 * term: the server reads it as blank.
 */
export function emptyMessage(total: number, term: string): string | null {
  if (total > 0) return null;
  return term.trim() === ''
    ? 'No players registered yet. Add one so results can be recorded against them.'
    : 'No players match that search.';
}

export function recordsStatus(input: { hasData: boolean; placeholder: boolean; failed: boolean }): RecordsStatus {
  if (!input.hasData) return input.failed ? 'failed' : 'loading';
  return input.placeholder ? 'updating' : 'ready';
}
